import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { whoAmI } from '../account/sharing';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLearner } from '../learners/LearnerContext';
import { useSettings } from '../settings/SettingsContext';
import { KINDS } from '../concepts/kinds';
import { isKnownKind, type ProgramView } from '../programs/types';
import {
  addProgramToPackage,
  createPackage,
  deletePackage,
  fetchPackageItems,
  fetchPackages,
  removePackageItem,
  renamePackage,
  type PackageItem,
  type ProgramPackage,
} from '../packages/packages';
import { translate, useT } from '../i18n/i18n';

function kindLabel(kind: string): string {
  return isKnownKind(kind) ? translate(KINDS[kind].label) : kind;
}

export default function PackagesPage() {
  const t = useT();
  const { settings } = useSettings();
  const { learnerId, learners } = useLearner();

  const [sets, setSets] = useState<ProgramPackage[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [items, setItems] = useState<PackageItem[]>([]);
  const [programs, setPrograms] = useState<ProgramView[]>([]);

  const [mode, setMode] = useState<'none' | 'new' | 'rename'>('none');
  const [nameValue, setNameValue] = useState('');
  const [chosenProgram, setChosenProgram] = useState('');
  const [pendingDelete, setPendingDelete] = useState<ProgramPackage | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState<boolean | null>(null);

  const enabled = settings.features.programPackages;
  const selected = sets.find((s) => s.id === selectedId) ?? null;

  const loadSets = useCallback(async (keep?: string) => {
    if (!enabled) { setSets([]); setLoading(false); return; }
    try {
      const got = await fetchPackages();
      setSets(got);
      setSelectedId((cur) => {
        const wanted = keep ?? cur;
        if (wanted && got.some((s) => s.id === wanted)) return wanted;
        return got.length > 0 ? got[0].id : '';
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('The sets could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { void loadSets(); }, [loadSets]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void whoAmI()
      .then((w) => { if (!cancelled) setIsOwner(w.ownAccount); })
      .catch(() => { if (!cancelled) setIsOwner(false); });
    return () => { cancelled = true; };
  }, [enabled]);

  /* THE LIST BELOW FOLLOWS THE CHOICE ABOVE. Whenever the selected set changes,
     its contents are fetched — there is nothing to expand and nothing to keep in
     step by hand. */
  useEffect(() => {
    if (!selectedId) { setItems([]); return; }
    let cancelled = false;
    void fetchPackageItems(selectedId)
      .then((got) => { if (!cancelled) setItems(got); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : t('That set could not be opened.')); });
    return () => { cancelled = true; };
  }, [selectedId]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const q = learnerId ? `?learnerId=${encodeURIComponent(learnerId)}` : '';
    void api<ProgramView[]>(`/programs${q}`, { token: true })
      .then((list) => { if (!cancelled) setPrograms(list); })
      .catch(() => { if (!cancelled) setPrograms([]); });
    return () => { cancelled = true; };
  }, [enabled, learnerId]);

  async function refreshItems(packageId: string) {
    try {
      setItems(await fetchPackageItems(packageId));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That set could not be read.'));
    }
  }

  async function saveName() {
    const name = nameValue.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'new') {
        const made = await createPackage(name);
        await loadSets(made.id);
      } else if (selected) {
        await renamePackage(selected.id, name);
        await loadSets(selected.id);
      }
      setMode('none');
      setNameValue('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That name could not be saved.'));
    } finally {
      setBusy(false);
    }
  }

  async function addProgram() {
    if (!selectedId || !chosenProgram) return;
    setBusy(true);
    setError(null);
    try {
      await addProgramToPackage(selectedId, chosenProgram);
      setChosenProgram('');
      await refreshItems(selectedId);
      await loadSets(selectedId);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That program could not be added.'));
    } finally {
      setBusy(false);
    }
  }

  async function removeItem(itemId: string) {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      await removePackageItem(selectedId, itemId);
      await refreshItems(selectedId);
      await loadSets(selectedId);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That program could not be removed.'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deletePackage(pendingDelete.id);
      setPendingDelete(null);
      setSelectedId('');
      await loadSets('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That set could not be deleted.'));
      setPendingDelete(null);
    } finally {
      setBusy(false);
    }
  }

  if (!enabled) {
    return (
      <section aria-labelledby="sets-h">
        <div className="card">
          <h2 id="sets-h">{t('Sets of programs')}</h2>
          <p>{t('This is switched off for your account.')}</p>
          <p className="field-hint">
            {t('Turn on {feature} under {section} in {link}, and it appears here.').split(/(\{feature\}|\{section\}|\{link\})/).map((part, i) =>
              part === '{feature}' ? <strong key={i}>{t('Add programs in a set')}</strong>
              : part === '{section}' ? <strong key={i}>{t('How teaching runs')}</strong>
              : part === '{link}' ? <Link key={i} to="/settings">{t('Settings → Features')}</Link>
              : part)}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="sets-h">
      <h2 id="sets-h" className="visually-hidden">{t('Sets of programs')}</h2>

      <div className="banner" role="region" aria-label={t('What to do here')}>
        <span className="muted">
          {t('A set is a group of programs you can add to a learner all at once. Programs are copied into a set as they are now, so changing a set later never rewrites what a learner is already working on.')}
        </span>
      </div>

      {error && <div className="error" role="alert">{error}</div>}

      <div className="card">
        {loading ? (
          <p className="muted">{t('Loading…')}</p>
        ) : mode === 'none' ? (
          <>
            <div className="row">
              <div className="field">
                <label htmlFor="set-pick">{t('Set')}</label>
                <select
                  id="set-pick"
                  value={selectedId}
                  disabled={busy || sets.length === 0}
                  onChange={(e) => { setSelectedId(e.target.value); setChosenProgram(''); setError(null); }}
                >
                  {sets.length === 0 && <option value="">{t('No sets yet')}</option>}
                  {sets.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} — {s.programCount === 1 ? t('1 program') : t('{n} programs', { n: s.programCount })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {isOwner === true && (
              <div className="toolbar">
                <button
                  className="btn"
                  onClick={() => { setMode('new'); setNameValue(''); setError(null); }}
                  disabled={busy}
                >
                  {t('Make a new set')}
                </button>
                <button
                  className="btn secondary"
                  onClick={() => { if (selected) { setMode('rename'); setNameValue(selected.name); setError(null); } }}
                  disabled={busy || !selected}
                >
                  {t('Rename')}
                </button>
                <button
                  className="btn secondary danger"
                  onClick={() => { if (selected) { setError(null); setPendingDelete(selected); } }}
                  disabled={busy || !selected}
                >
                  {t('Delete')}
                </button>
              </div>
            )}
            {isOwner === false && (
              <p className="field-hint">
                {t('Sets are made and changed by whoever owns this account. You can add any of them to a learner from the home page.')}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="row">
              <div className="field">
                <label htmlFor="set-name">
                  {mode === 'new' ? t('Name the new set') : t('New name')}
                </label>
                <input
                  id="set-name"
                  value={nameValue}
                  maxLength={120}
                  placeholder={t('Starting set')}
                  disabled={busy}
                  autoFocus
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void saveName(); }
                    if (e.key === 'Escape') { setMode('none'); setNameValue(''); }
                  }}
                />
              </div>
            </div>
            <div className="toolbar">
              <button className="btn" onClick={() => void saveName()} disabled={busy || !nameValue.trim()}>
                {mode === 'new' ? t('Make this set') : t('Save name')}
              </button>
              <button
                className="btn secondary"
                onClick={() => { setMode('none'); setNameValue(''); }}
                disabled={busy}
              >
                {t('Cancel')}
              </button>
            </div>
          </>
        )}
      </div>

      {!loading && mode === 'none' && selected && (
        <section className="card" aria-label={t('Programs in {set}', { set: selected.name })}>
          <h2>{t('Programs in {set}', { set: selected.name })}</h2>

          {items.length === 0 ? (
            <p className="muted">
              {isOwner === true
                ? t('Nothing in this set yet. Add a program below.')
                : t('Nothing in this set yet.')}
            </p>
          ) : (
            <ul className="list">
              {items.map((it) => (
                <li className="list-item" key={it.id}>
                  <div className="list-item-text">
                    <div className="name">{it.name}</div>
                    <div className="list-item-meta">{kindLabel(it.kind)}</div>
                  </div>
                  {isOwner === true && (
                    <div className="toolbar">
                      <button
                        className="btn secondary"
                        onClick={() => void removeItem(it.id)}
                        disabled={busy}
                        aria-label={t('Remove {program} from {set}', { program: it.name, set: selected.name })}
                      >
                        {t('Remove')}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {isOwner === true && (
            <>
          <div className="row">
            <div className="field">
              <label htmlFor="add-program">{t('Add a program')}</label>
              <select
                id="add-program"
                value={chosenProgram}
                disabled={busy || programs.length === 0}
                onChange={(e) => setChosenProgram(e.target.value)}
              >
                <option value="">{t('Choose a program…')}</option>
                {programs.map((prog) => (
                  <option key={prog.id} value={prog.id}>{prog.name}</option>
                ))}
              </select>
              <span className="field-hint">
                {programs.length === 0
                  ? t('This learner has no programs yet.')
                  : learners.length > 1
                    ? t('These are the selected learner’s programs. Switch learner above to add another’s.')
                    : t('A copy is taken as the program is now.')}
              </span>
            </div>
          </div>
          <div className="toolbar">
            <button className="btn" onClick={() => void addProgram()} disabled={busy || !chosenProgram}>
              {t('Add to this set')}
            </button>
          </div>
          </>
          )}
        </section>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('Delete this set?')}
        message={
          pendingDelete
            ? (pendingDelete.programCount === 1
              ? t('"{set}" and the program copied into it will be removed. Programs already added to a learner are not affected. This cannot be undone.', { set: pendingDelete.name })
              : t('"{set}" and the {n} programs copied into it will be removed. Programs already added to a learner are not affected. This cannot be undone.', { set: pendingDelete.name, n: pendingDelete.programCount }))
            : ''
        }
        confirmLabel={busy ? t('Deleting…') : t('Delete')}
        cancelLabel={t('Keep')}
        destructive
        onConfirm={() => void confirmDelete()}
        onCancel={() => { if (!busy) setPendingDelete(null); }}
      />
    </section>
  );
}
